import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';
import { fadeInOnScroll, scaleOnScroll } from '../utils/animations';
import { projects } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const ProjectDetail = () => {
  const { id } = useParams();
  const project = projects.find((p) => p.id === parseInt(id));
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    // Hero image reveal
    if (heroRef.current) {
      gsap.fromTo(
        heroRef.current,
        { clipPath: 'inset(100% 0 0 0)' },
        { clipPath: 'inset(0% 0 0 0)', duration: 1.5, ease: 'power4.out' }
      );
    }

    // Content fade in
    if (contentRef.current) {
      const elements = contentRef.current.querySelectorAll('.fade-in');
      fadeInOnScroll(elements);
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-headline text-4xl font-bold mb-4">Project Not Found</h1>
          <Link to="/work" className="text-[var(--primary)] hover:brightness-110">
            Back to Work
          </Link>
        </div>
      </div>
    );
  }

  const nextProject = projects[(project.id % projects.length)];

  return (
    <div className="project-detail-page pt-20">
      {/* Hero Section */}
      <section className="relative h-screen flex items-end">
        <div
          ref={heroRef}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${project.image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--background)] via-[var(--background)]/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-20 w-full">
          {/* Back Button */}
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-[var(--primary)] font-headline text-sm font-bold uppercase tracking-code mb-12 hover:gap-3 transition-all group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Back to Work
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
            <div>
              <span className="inline-block px-3 py-1 bg-[var(--secondary-container)] text-[var(--on-secondary-container)] font-body text-xs tracking-code uppercase mb-6 rounded-sm">
                {project.category}
              </span>
              <h1 className="font-headline text-5xl md:text-7xl font-bold tracking-headline uppercase leading-tight">
                {project.title}
              </h1>
            </div>

            <div>
              <p className="font-body text-[var(--on-surface-variant)] text-lg leading-relaxed mb-8">
                {project.description}
              </p>
              {project.tags && (
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-[var(--surface-container-high)] text-[var(--on-surface)] text-xs font-bold tracking-code uppercase rounded-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section ref={contentRef} className="py-24 max-w-5xl mx-auto px-6 lg:px-8">
        {/* Overview */}
        <div className="fade-in mb-20">
          <h2 className="font-headline text-3xl md:text-4xl font-bold tracking-tight uppercase mb-8 text-[var(--primary)]">
            Project Overview
          </h2>
          <p className="font-body text-[var(--on-surface-variant)] text-lg leading-relaxed mb-6">
            This project showcases our expertise in building high-performance, scalable systems that push the boundaries of what's possible with modern technology. Our team worked closely with the client to deliver a solution that exceeded expectations.
          </p>
          <p className="font-body text-[var(--on-surface-variant)] text-lg leading-relaxed">
            Through rigorous technical planning and iterative development, we created a robust architecture that handles millions of transactions while maintaining exceptional performance metrics.
          </p>
        </div>

        {/* Challenge */}
        <div className="fade-in mb-20">
          <h2 className="font-headline text-3xl md:text-4xl font-bold tracking-tight uppercase mb-8">
            The <span className="text-[var(--primary)]">Challenge</span>
          </h2>
          <div className="bg-[var(--surface-container-low)] p-8 lg:p-12 border-l-4 border-[var(--primary)]">
            <p className="font-body text-[var(--on-surface-variant)] text-lg leading-relaxed">
              The client needed a system that could scale from startup MVP to enterprise-level platform while maintaining sub-second response times and 99.99% uptime. Traditional architectures wouldn't suffice for their ambitious vision.
            </p>
          </div>
        </div>

        {/* Solution */}
        <div className="fade-in mb-20">
          <h2 className="font-headline text-3xl md:text-4xl font-bold tracking-tight uppercase mb-8">
            Our <span className="text-[var(--primary)]">Solution</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[var(--surface-container)] p-8 hover:bg-[var(--surface-container-high)] transition-all duration-300">
              <h3 className="font-headline text-xl font-bold uppercase mb-4">Architecture</h3>
              <p className="font-body text-[var(--on-surface-variant)] leading-relaxed">
                Microservices-based architecture with event-driven communication, enabling independent scaling and deployment of each service.
              </p>
            </div>
            <div className="bg-[var(--surface-container)] p-8 hover:bg-[var(--surface-container-high)] transition-all duration-300">
              <h3 className="font-headline text-xl font-bold uppercase mb-4">Technology</h3>
              <p className="font-body text-[var(--on-surface-variant)] leading-relaxed">
                Leveraged cutting-edge frameworks and cloud-native tools to build a resilient, high-performance system.
              </p>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="fade-in mb-20">
          <h2 className="font-headline text-3xl md:text-4xl font-bold tracking-tight uppercase mb-8">
            The <span className="text-[var(--primary)]">Results</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '10M+', label: 'Daily Requests' },
              { value: '99.99%', label: 'Uptime' },
              { value: '<200ms', label: 'Response Time' },
              { value: '50%', label: 'Cost Reduction' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-6 bg-[var(--surface-container-low)]">
                <div className="font-headline text-3xl lg:text-4xl font-bold text-[var(--primary)] mb-2">
                  {stat.value}
                </div>
                <div className="font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)]">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="fade-in text-center border-t border-[var(--outline-variant)]/20 pt-16">
          <p className="font-body text-[var(--on-surface-variant)] text-lg mb-8">
            Interested in a similar project for your business?
          </p>
          <Link
            to="/contact"
            className="magnetic inline-flex items-center gap-2 bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-10 py-4 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 hover:scale-105 transition-all duration-300 rounded-none group"
          >
            Let's Talk
            <ExternalLink size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Next Project */}
      <section className="py-24 bg-[var(--surface-container-lowest)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-[var(--on-surface-variant)] text-sm tracking-code uppercase">
              Next Project
            </span>
          </div>
          <Link
            to={`/work/${nextProject.id}`}
            className="group block relative overflow-hidden h-[60vh]"
          >
            <img
              src={nextProject.image}
              alt={nextProject.title}
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--background)] via-transparent to-transparent flex items-end justify-center pb-12">
              <div className="text-center">
                <h3 className="font-headline text-4xl md:text-6xl font-bold tracking-headline uppercase mb-4">
                  {nextProject.title}
                </h3>
                <div className="flex items-center justify-center gap-2 text-[var(--primary)] font-headline text-sm font-bold uppercase tracking-code group-hover:gap-3 transition-all">
                  View Case Study
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
